import React, { useState, useEffect } from "react";
import Aos from "aos";
import "aos/dist/aos.css";
import { vpsData } from "../assets/data/vpsData";
import SubscriptionModal from "./SubscriptionModal/SubscriptionModal";

const HostingPricing = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("vps");

  useEffect(() => {
    Aos.init({ duration: 2000 });
  }, []);

  const handleOpenModal = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPlan(null);
  };

  const vpsPlans = vpsData.filter(p => p.type === "vps" || !p.isStorageOnly);
  const storagePlans = vpsData.filter(p => p.isStorageOnly);

  const openCustomBuild = () => handleOpenModal({ plan: "Custom", isCustom: true });

  return (
    <div className='lg:pl-20 p-10 lg:pr-20 2xl:pr-60 2xl:pl-60' data-aos="fade-up" data-aos-offset="50">
      <div className='bg-keysColor rounded-3xl p-10 lg:pt-6 2xl:pt-14 xl:pt-10 md:w-full md:h-full flex-wrap lg:w-full lg:h-auto h-auto w-full xl:pl-10 xl-pr-10 lg:pl-16 lg:pr-16 lg:pb-16'>
        
        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="bg-[#001F3F] rounded-xl p-1 flex">
            <button
              onClick={() => setActiveTab("vps")}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                activeTab === "vps"
                  ? "bg-[#409B1F] text-white"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              VPS Hosting
            </button>
            <button
              onClick={() => setActiveTab("storage")}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                activeTab === "storage"
                  ? "bg-[#409B1F] text-white"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              Cloud Storage
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-12">
          {/* Custom build banner */}
          <div className="bg-[#001F3F] border-2 border-dashed border-[#409B1F] rounded-3xl p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div>
              <h3 className="text-white text-xl font-bold">Need something custom?</h3>
              <p className="text-gray-300 text-sm mt-1">
                Enter your own vCPU, RAM and storage specs — price with NHIL, GETFund & VAT calculated live.
              </p>
            </div>
            <button
              onClick={openCustomBuild}
              className="bg-[#409B1F] hover:bg-[#357C19] text-white font-semibold px-6 py-2 rounded-xl transition-all duration-300 whitespace-nowrap"
            >
              Build Your Own →
            </button>
          </div>

          {/* VPS Hosting Pricing */}
          {activeTab === "vps" && (
            <div>
              <h3 className='text-white text-2xl leading-snug font-bold text-center md:text-dataRent md:block mb-6'>
                VPS Package Pricing
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 2xl:gap-10">
                {vpsPlans.map((plan, index) => (
                  <div
                    key={index}
                    className="bg-[#001F3F] border-2 border-[#022A52] rounded-3xl p-6 flex flex-col justify-between hover:shadow-lg transition-all duration-300"
                    data-aos={index % 2 === 0 ? "fade-up" : "fade-right"}
                  >
                    <div>
                      <h3 className="text-2xl font-semibold text-white mb-2 text-left">
                        {plan.plan}
                      </h3>
                      <ul className="text-white mb-4 text-sm space-y-1 text-left">
                        <li>CPU: {plan.cpu}</li>
                        <li>RAM: {plan.ram}</li>
                        <li>Storage: {plan.storage}</li>
                      </ul>
                      <div className="text-footerText mb-4 bg-heroRent p-2 rounded-2xl text-sm text-left">
                        <p className="text-[#fff]">
                          <strong>Linux:</strong> <span className="text-[#fff]">{plan.osOptions.linux.price}/month</span>
                        </p>
                        <p className="text-[#fff]">
                          <strong>Windows:</strong> <span className="text-[#fff]">{plan.osOptions.windows.price}/month</span>
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleOpenModal(plan)}
                      className="mt-4 w-full bg-[#409B1F] hover:bg-[#357C19] text-white font-semibold py-2 rounded-xl transition-all duration-300"
                    >
                      Get Started
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cloud Storage Pricing */}
          {activeTab === "storage" && (
            <div>
              <h3 className='text-white text-2xl leading-snug font-bold text-center md:text-dataRent md:block mb-6'>
                Cloud Storage Plans
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 2xl:gap-10">
                {storagePlans.map((plan, index) => (
                  <div
                    key={index}
                    className="bg-[#001F3F] border-2 border-[#022A52] rounded-3xl p-6 flex flex-col justify-between hover:shadow-lg transition-all duration-300"
                    data-aos={index % 2 === 0 ? "fade-up" : "fade-right"}
                  >
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-2xl font-semibold text-white text-left">
                          {plan.plan}
                        </h3>
                        <span className="bg-[#409B1F]/20 text-[#409B1F] text-xs px-2 py-1 rounded-full">
                          Storage
                        </span>
                      </div>
                      <ul className="text-white mb-4 text-sm space-y-1 text-left">
                        <li>Storage: {plan.storage}</li>
                        <li className="text-gray-400 text-xs">Pure cloud storage without compute</li>
                      </ul>
                      <div className="text-footerText mb-4 bg-heroRent p-2 rounded-2xl text-sm text-left">
                        <p className="text-[#fff]">
                          <strong>Price:</strong> <span className="text-[#fff]">{plan.osOptions.storage.price}/month</span>
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleOpenModal(plan)}
                      className="mt-4 w-full bg-[#409B1F] hover:bg-[#357C19] text-white font-semibold py-2 rounded-xl transition-all duration-300"
                    >
                      Get Started
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Modal */}
        {isModalOpen && (
          <SubscriptionModal 
            key={selectedPlan?.plan || "subscription-modal"}
            plan={selectedPlan} 
            onClose={handleCloseModal} 
          />
        )}
      </div>
    </div>
  );
};

export default HostingPricing;